import React, { useState, useEffect } from "react";
import propTypes from "prop-types";
import Collapsible from "react-collapsible";
import classnames from "classnames";
import Icon from "../icons/Icon";
import { guid, createDataAttributes } from "../../utilities/index";

import "./Accordion.scss";

// https://github.com/glennflanagan/react-collapsible

const Accordion = ({
  id = guid(),
  accordionIndex,
  children,
  isOpen,
  onClick,
  triggerContent,
  openedClassName,
  className,
  overflowWhenOpen,
  textMain,
  textExtra,
  stackedText,
  smallTextExtra,
  textRight,
  dataAttrib,
  ...rest
}) => {
  const [isExpanded, setExpandedState] = useState(isOpen);

  useEffect(() => {
    if (isOpen !== undefined) setExpandedState(isOpen);
  }, [isOpen]);

  // update the expanded state immediately on click so the icon rotates during content animation
  const onOpening = () => {
    setExpandedState(true);
  };
  const onClosing = () => {
    setExpandedState(false);
  };

  // send callback to onClick fn
  const onOpen = () => {
    const callback = { accordionIndex, open: true };
    if (onClick) onClick(callback);
  };
  const onClose = () => {
    const callback = { accordionIndex, open: false };
    if (onClick) onClick(callback);
  };

  const triggerContainer = (
    <span
      className="uikit__accordion__trigger-container"
      {...createDataAttributes(dataAttrib)}
    >
      <span
        className={classnames("uikit__accordion__trigger-title", {
          stackedText,
          smallTextExtra
        })}
      >
        {triggerContent ? (
          <span className="uikit__accordion__trigger-element">
            {triggerContent}
          </span>
        ) : (
          <>
            <span
              id={`uikit_accordion_title_${id}`}
              className="uikit__accordion__trigger-textMain"
            >
              {textMain}
            </span>
            {textExtra && (
              <span className="uikit__accordion__trigger-textExtra">
                {textExtra}
              </span>
            )}
          </>
        )}
      </span>
      {textRight && (
        <span className="uikit__accordion__rightSide-container">
          {textRight}
        </span>
      )}
      <span
        className={classnames(
          "uikit__accordion__chevron-wrapper uikit__icon-wrapper",
          {
            expanded: isExpanded
          }
        )}
      >
        <Icon icon="fasChevronCircleDown" />
      </span>
      <button
        aria-labelledby={`uikit_accordion_title_${id}`}
        className="uikit-sr-only"
      />
    </span>
  );

  const clossedClass = classnames(className);
  const openedClass = classnames(
    "uikit__accordion__is-open",
    className,
    openedClassName
  );

  return (
    <Collapsible
      contentElementId={id && `uikit_collapsible_content_${id}`}
      className={clossedClass}
      openedClassName={openedClass}
      open={isOpen}
      trigger={triggerContainer}
      triggerElementProps={id && { id: `uikit_collapsible_trigger_${id}` }}
      classParentString="uikit__accordion"
      onOpen={onOpen}
      onClose={onClose}
      overflowWhenOpen={overflowWhenOpen}
      onOpening={onOpening}
      onClosing={onClosing}
      contentOuterClassName="uikit__accordion__content"
      contentInnerClassName="uikit__accordion__content-inner"
      {...createDataAttributes(dataAttrib)}
      {...rest}
    >
      {children}
    </Collapsible>
  );
};

Accordion.propTypes = {
  id: propTypes.string,
  children: propTypes.oneOfType([propTypes.node, propTypes.element]).isRequired,
  /** sets open/closed state  */
  isOpen: propTypes.bool,
  /** function passes back the clicked accordion's index number */
  onClick: propTypes.func,
  /** the accordion's index number */
  accordionIndex: propTypes.number,
  /** the large text in the accordion panel: String or Element */
  textMain: propTypes.oneOfType([propTypes.string, propTypes.element]),
  /** the smaller text in the middle of the accordion panel: String or Element */
  textExtra: propTypes.oneOfType([propTypes.string, propTypes.element]),
  /** custom element on right side of trigger: String or Element */
  textRight: propTypes.oneOfType([propTypes.string, propTypes.element]),
  /** is the secondary text under the main text? */
  stackedText: propTypes.bool,
  /** make secondary text small */
  smallTextExtra: propTypes.bool,
  /** custom element for the content of the accordion panel */
  triggerContent: propTypes.element,
  /** a custom class name to set on the accordion container */
  className: propTypes.string,
  /** a custom class name to set when the accordion is open */
  openedClassName: propTypes.string,
  /** The CSS overflow property once the Collapsible is open. This can be any one of the valid CSS values of 'hidden', 'visible', 'auto', 'scroll', 'inherit', 'initial', or 'unset' */
  overflowWhenOpen: propTypes.oneOf([
    "hidden",
    "visible",
    "auto",
    "scroll",
    "inherit",
    "initial",
    "unset"
  ]),
  dataAttrib: propTypes.arrayOf(Object),
  dataWrapperAttrib: propTypes.arrayOf(Object)
};

Accordion.defaultProps = {
  className: "",
  overflowWhenOpen: "visible",
  stackedText: false,
  onClick: () => {}
};

export default Accordion;
